import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { swrKeys } from '@shared/api/swr/keys';
import { stableWalletMonitoringParams } from '@shared/api/lib/wallet-monitoring-query';
import type {
  WalletGroupsMonitoringResponse,
  WalletsMonitoringResponse,
} from '@shared/api/contracts';
import {
  buildWalletGroupSections,
  mergePagedLists,
  monitoringPageCount,
  toWalletFilterCatalog,
  toWalletMonitoringQuery,
  WALLET_MONITORING_FETCH_LIMIT,
  type WalletsMonitoringFilters,
} from '@entities/wallets';
import { useApiServices } from '@shared/lib/use-api-services';

const SEARCH_DEBOUNCE_MS = 300;

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);

  return debounced;
};

export const useWalletsMonitoring = (filters: WalletsMonitoringFilters) => {
  const services = useApiServices();
  const debouncedSearch = useDebouncedValue(filters.search, SEARCH_DEBOUNCE_MS);

  const {
    data: groupsData,
    error: groupsError,
    isLoading: isGroupsLoading,
    mutate: mutateGroups,
  } = useSWR<WalletGroupsMonitoringResponse>(swrKeys.wallets.groups());

  const groups = useMemo(() => groupsData?.data ?? [], [groupsData?.data]);
  const filterCatalog = useMemo(
    () => toWalletFilterCatalog(groupsData?.facets),
    [groupsData?.facets],
  );

  const queryParams = useMemo(
    () =>
      toWalletMonitoringQuery(
        {
          ...filters,
          search: debouncedSearch,
        },
        1,
        WALLET_MONITORING_FETCH_LIMIT,
        {
          groupCodes: groups.map((group) => group.code),
          networkCodes: filterCatalog.networkCodes,
          currencyCodes: filterCatalog.currencyCodes,
        },
      ),
    [filters, debouncedSearch, groups, filterCatalog],
  );

  const walletsKey = useMemo(() => {
    if (!groupsData && !groupsError) {
      return null;
    }

    return swrKeys.wallets.all(stableWalletMonitoringParams(queryParams));
  }, [groupsData, groupsError, queryParams]);

  const {
    data: walletsData,
    error: walletsError,
    isLoading: isWalletsLoading,
    mutate: mutateWallets,
  } = useSWR<WalletsMonitoringResponse>(walletsKey, async () => {
    const first = await services.wallets.getWalletsMonitoring(queryParams);
    const totalPages = monitoringPageCount(first.pagination?.totalPages);
    if (totalPages === 1) return first;

    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        services.wallets.getWalletsMonitoring({ ...queryParams, page: index + 2 }),
      ),
    );
    return mergePagedLists([first, ...rest]);
  });

  const wallets = useMemo(() => walletsData?.data ?? [], [walletsData?.data]);
  const summary = walletsData?.summary ?? groupsData?.summary;
  const groupSummaries = useMemo(
    () => walletsData?.groupSummaries ?? [],
    [walletsData?.groupSummaries],
  );

  const sections = useMemo(
    () => buildWalletGroupSections(groups, wallets, groupSummaries),
    [groups, wallets, groupSummaries],
  );

  const totalWallets = Number(summary?.totalWallets ?? wallets.length);

  const mutate = async () => {
    await Promise.all([mutateGroups(), mutateWallets()]);
  };

  return {
    groups,
    wallets,
    sections,
    summary,
    filterCatalog,
    groupSummaries,
    totalWallets,
    isGroupsLoading,
    isWalletsLoading,
    isLoading: isGroupsLoading || isWalletsLoading,
    groupsError,
    walletsError,
    mutateGroups,
    mutateWallets,
    mutate,
  };
};
